import { Preferences } from '@capacitor/preferences';
import { apiGet } from '../widget-api/http-client';

export type OfflineCacheBooking = {
  lesson_name?: string;
  starts_at?: string;
  ends_at?: string;
  pin_prefix?: string;
  qr_data_url?: string;
};

export type OfflineCachePayload = {
  org_slug?: string;
  org_id?: number;
  user_id?: number;
  client_name?: string;
  client_secret?: string;
  otp_interval?: number;
  bookings: OfflineCacheBooking[];
  cached_at: string;
};

export const OFFLINE_CACHE_KEY = 'domcrm_offline_cache';

export async function saveOfflineCache(payload: OfflineCachePayload): Promise<void> {
  const json = JSON.stringify(payload);
  try {
    await Preferences.set({ key: OFFLINE_CACHE_KEY, value: json });
  } catch {
    // ignore
  }

  try {
    const win = window as unknown as { Capacitor?: { Plugins?: { Preferences?: { set: (opt: { key: string; value: string }) => Promise<void> } } } };
    if (win.Capacitor?.Plugins?.Preferences?.set) {
      await win.Capacitor.Plugins.Preferences.set({ key: OFFLINE_CACHE_KEY, value: json });
    }
  } catch {
    // ignore
  }

  try {
    localStorage.setItem(OFFLINE_CACHE_KEY, json);
  } catch {
    /* офлайн-кэш не критичен для онлайн-сессии */
  }

  // В мобильном приложении embed запускается во встроенном iframe
  // (local-бандл на своём origin): нативный Capacitor-мост здесь недоступен,
  // поэтому передаём кэш родителю через postMessage — он сохранит его
  // в нативные Preferences, которые читает офлайн-экран.
  try {
    const parentWin = window.parent as Window | null;
    if (parentWin && parentWin !== window) {
      parentWin.postMessage({ type: 'domcrm:offline-cache', payload }, '*');
    }
  } catch {
    // ignore
  }
}

export async function syncOfflineCacheInBackground(
  accessToken: string,
  me: { id: number; full_name?: string | null; organization_id?: number | null } | null,
  orgSlug?: string | null,
): Promise<void> {
  if (!accessToken || !me) return;
  const q: Record<string, string | number> = {};
  if (orgSlug) q.org_slug = orgSlug;
  if (me.organization_id) q.org_id = me.organization_id;

  try {
    let secret: string | undefined;
    try {
      const setup = await apiGet<{ otp_secret?: string }>('/api/v1/qrcode/totp-setup', q, { accessToken });
      secret = setup?.otp_secret;
    } catch {
      // ignore
    }

    let bookings: OfflineCacheBooking[] = [];
    try {
      const list = await apiGet<{ items?: OfflineCacheBooking[] } | OfflineCacheBooking[]>(
        '/api/v1/qrcode/codes/active',
        { limit: '20', ...q },
        { accessToken },
      );
      if (Array.isArray(list)) {
        bookings = list;
      } else if (list && Array.isArray(list.items)) {
        bookings = list.items;
      }
    } catch {
      // ignore
    }

    if (secret) {
      await saveOfflineCache({
        org_slug: orgSlug ?? undefined,
        org_id: me.organization_id ?? undefined,
        user_id: me.id,
        client_name: me.full_name ?? undefined,
        client_secret: secret,
        otp_interval: 30,
        bookings: bookings.map((b) => ({
          lesson_name: b.lesson_name,
          starts_at: b.starts_at,
          ends_at: b.ends_at,
          pin_prefix: b.pin_prefix,
          qr_data_url: b.qr_data_url,
        })),
        cached_at: new Date().toISOString(),
      });
    }
  } catch {
    // best-effort silent background cache
  }
}
